import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Save as SaveIcon } from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../hooks/redux";
import {
  fetchTerms,
  saveTerms,
  clearSaved,
} from "../store/slices/termsandconditionSlice";
import { toast } from "react-toastify";
import SkeletonLoader from "../components/common/SkeletonLoader";
import RichTextEditor from "../components/common/RichTextEditor";
import { productAPI, categoryAPI } from "../services/api";

const TermsAndConditions: React.FC = () => {
  const [saving, setSaving] = useState(false);
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);
  const dispatch = useAppDispatch();
  const { content, loading, error, saved } = useAppSelector(
    (state) => state.terms
  );

  // Use simple HTML string now (RichTextEditor returns HTML)
  const [editorHtml, setEditorHtml] = useState("");
  const [isContentLoaded, setIsContentLoaded] = useState(false);

  useEffect(() => {
    dispatch(fetchTerms());
  }, [dispatch]);

  useEffect(() => {
    // When fetched content (HTML string) arrives, just set it directly
    if (typeof content === "string") {
      setEditorHtml(content);
      setIsContentLoaded(true);
    }
  }, [content]);

  useEffect(() => {
    if (saved) {
      toast.success("Terms & Conditions saved successfully!");
      dispatch(clearSaved());
      dispatch(fetchTerms());
    }
  }, [saved, dispatch]);

  // Image upload for editor (optional)
  const uploadTermsImage = async (file: File): Promise<string> => {
    // Validate file size (2MB max)
    const maxSize = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSize) {
      toast.error("Image size must be less than 2MB");
      throw new Error("Image size must be less than 2MB");
    }
    try {
      const formData = new FormData();
      formData.append("fileKey", file, file.name);
      const res = await productAPI.uploadProductImages(formData);
      const uploadedFile = res.data.result.files.fileKey[0];
      return categoryAPI.getMediaDownloadUrl
        ? categoryAPI.getMediaDownloadUrl(uploadedFile.name)
        : uploadedFile.name;
    } catch (e) {
      toast.error("Image upload failed");
      throw e;
    }
  };

  const handleSave = async () => {
    // Validate: not allow only spaces or empty
    const plainText = editorHtml.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim();
    if (!plainText) {
      toast.error("Terms & Conditions content cannot be empty or only spaces.");
      return;
    }
    setSaving(true);
    try {
      await dispatch(saveTerms(editorHtml)).unwrap();
    } catch (e: any) {
      toast.error(e?.message || "Failed to save terms & conditions");
    } finally {
      setSaving(false);
    }
  }

  if (loading && !isContentLoaded) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
          Terms & Conditions
        </Typography>
        <SkeletonLoader type="table" count={6} />
      </Box>
    );
  }

  return (
  <Box sx={{ position: 'relative' }}>
      <Typography
        variant="h4"
        gutterBottom
        sx={{ mb: 4, fontSize: "1.25rem", fontWeight: "bold" }}
      >
        Terms & Conditions
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3, position: 'relative' }}>
        <CardContent>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ mb: 3, fontSize: "1rem", fontWeight: "bold" }}
          >
            Edit Terms & Conditions Content
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            You can upload image files up to 2MB (JPEG, PNG, GIF).
          </Typography>

          {/* Loader overlay for saving */}
          {saving && (
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              bgcolor: 'rgba(255,255,255,0.7)',
              zIndex: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Typography sx={{ mr: 2, fontWeight: 600, color: 'primary.main' }}>Saving...</Typography>
              <CircularProgress color="primary" />
            </Box>
          )}

          {!isContentLoaded ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <RichTextEditor
              value={editorHtml}
              onChange={(html) => setEditorHtml(html)}
              placeholder="Start typing your terms & conditions content..."
              maxChars={5000}
              minHeight={400}
              uploadImage={uploadTermsImage}
              label={null}
              disabled={saving}
            />
          )}
        </CardContent>
      </Card>

      <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving || !isContentLoaded}
          sx={{
            textTransform: "none",
            bgcolor: "secondary.main",
            "&:hover": {
              bgcolor: "primary.main",
            },
          }}
        >
          {saving ? <CircularProgress size={20} color="inherit" /> : "Save Terms"}
        </Button>
      </Box>
    </Box>
  );
};

export default TermsAndConditions;